import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Instagram, MessageCircle } from 'lucide-react';
import { useFooter, useSiteConfig, useContact } from '../../context/CMSContext';

const Footer: React.FC = () => {
  const footer = useFooter();
  const siteConfig = useSiteConfig();
  const contact = useContact();

  const copyright = footer.copyright.replace('{year}', new Date().getFullYear().toString());

  return (
    <footer className="bg-slate-900 text-slate-400 py-16">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 mb-12">
          <div className="col-span-2">
            <h3 className="text-white text-xl font-bold mb-2">{footer.companyName}</h3>
            <p className="text-white/60 text-sm mb-4">{footer.companyNameEnglish}</p>
            <p className="text-sm leading-relaxed max-w-xs">{footer.description}</p>
            <div className="mt-6 flex gap-4">
              {siteConfig.social.facebook && (
                <a
                  href={siteConfig.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-white/10 p-2 hover:bg-gold-600 hover:text-white transition-colors"
                >
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {siteConfig.social.instagram && (
                <a
                  href={siteConfig.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-white/10 p-2 hover:bg-gold-600 hover:text-white transition-colors"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {siteConfig.social.whatsapp1 && (
                <a
                  href={siteConfig.social.whatsapp1}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-white/10 p-2 hover:bg-green-600 hover:text-white transition-colors"
                >
                  <MessageCircle className="h-5 w-5" />
                </a>
              )}
            </div>
          </div>

          <div>
            <h4 className="text-white font-bold mb-6">快速連結</h4>
            <ul className="space-y-4 text-sm">
              {footer.links
                .filter((link) => link.href !== '/admin')
                .map((link) => (
                  <li key={link.href}>
                    <Link to={link.href} className="hover:text-gold-500 transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-6">手機瀏覽</h4>
            <div className="inline-block rounded-lg bg-white p-2">
              <img
                src="/images/qr-code.png"
                alt="金晉物業網站 QR Code"
                className="h-28 w-28"
              />
            </div>
            <p className="mt-3 text-xs text-slate-500">掃描查看公司網站</p>
          </div>

          <div className="col-span-2">
            <h4 className="text-white font-bold mb-6">聯絡我們</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gold-600 shrink-0" />
                <a
                  href={contact.info.mapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  {contact.info.addressDisplay}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gold-600 shrink-0" />
                <a href={`tel:${contact.info.phone}`} className="hover:text-white transition-colors">
                  {contact.info.phoneDisplay}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gold-600 shrink-0" />
                <a href={`mailto:${contact.info.email}`} className="hover:text-white transition-colors">
                  {contact.info.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-slate-800 text-center text-xs space-y-2">
          <p>{copyright}</p>
          {footer.licenseNumber && (
            <p className="text-slate-500">牌照號碼：{footer.licenseNumber}</p>
          )}
          <p className="text-slate-500">
            <Link to="/privacy" className="hover:text-gold-500 transition-colors">私隱政策</Link>
            {' · '}
            Powered by{' '}
            <a
              href="https://jkdcoding.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-gold-500 hover:underline"
            >
              JKDCoding
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
